import { API_BASE_URL } from "config";

interface FetcherConfig {
  baseURL?: string;
  headers?: Record<string, string>;
}

interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<any, any>;
  retry?: boolean;
}

const fetcher = {
  config: {
    baseURL: API_BASE_URL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  },

  configure(config: FetcherConfig) {
    this.config = {
      ...this.config,
      ...config,
      headers: {
        ...this.config.headers,
        ...config.headers,
      },
    };
    return this;
  },

  buildURL(url: string, params?: Record<string, string>): string {
    const baseURL = this.config.baseURL?.replace(/\/$/, "");
    const normalizedURL = url.startsWith("/") ? url : `/${url}`;
    let fullURL = baseURL ? `${baseURL}${normalizedURL}` : normalizedURL;

    if (params) {
      const query = new URLSearchParams(params).toString();
      fullURL += `?${query}`;
    }

    return fullURL;
  },

  async prepareHeaders(options?: RequestOptions): Promise<Headers> {
    const headers = new Headers(this.config.headers);

    if (options?.headers) {
      Object.entries(options.headers).forEach(([k, v]) => headers.set(k, v));
    }

    // Get token from localStorage
    let authToken = localStorage.getItem("access_token");
    if (authToken) authToken = authToken.replace(/"/g, "");

    if (authToken) {
      headers.set("Authorization", `Bearer ${authToken}`);
    }

    return headers;
  },

  async request<T = any>(
    method: string,
    url: string,
    data?: any,
    options: RequestOptions = {},
  ): Promise<T> {
    const headers = await this.prepareHeaders(options);
    const fullURL = this.buildURL(url, options.params);
    const retry = options.retry ?? true;

    try {
      if (data instanceof FormData) {
        headers.delete("Content-Type");
      }

      const requestOptions: RequestInit = {
        method,
        headers,
        body:
          data instanceof FormData
            ? data
            : data
              ? JSON.stringify(data)
              : undefined,
      };

      const response = await fetch(fullURL, requestOptions);
      const responseData = await response.json().catch(() => ({}));

      // Global 401 handler
      if (response.status === 401 && retry) {
        const refreshed = await refreshTokenRequest();

        if (refreshed) {
          return this.request(method, url, data, { ...options, retry: false });
        }

        localStorage.clear();

        return Promise.reject({
          response: { status: 401, data: responseData },
        });
      }

      if (!response.ok) {
        const error: any = new Error(`HTTP error! status: ${response.status}`);
        error.response = responseData;
        throw error;
      }

      return responseData as T;
    } catch (error) {
      throw error;
    }
  },

  get(url: string, options: RequestOptions = {}) {
    return this.request("GET", url, undefined, options);
  },

  post(url: string, data?: any, options: RequestOptions = {}) {
    return this.request("POST", url, data, options);
  },

  put(url: string, data?: any, options: RequestOptions = {}) {
    return this.request("PUT", url, data, options);
  },

  patch(url: string, data?: any, options: RequestOptions = {}) {
    return this.request("PATCH", url, data, options);
  },

  delete(url: string, options: RequestOptions = {}) {
    return this.request("DELETE", url, undefined, options);
  },

  postForm(url: string, formData: FormData, options: RequestOptions = {}) {
    return this.request("POST", url, formData, options);
  },
};

async function refreshTokenRequest(): Promise<boolean> {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return false;

  try {
    const res = await fetch(fetcher.buildURL("/auth/token/refresh"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        refresh_token: refreshToken.replace(/"/g, ""),
      }),
    });

    const data = await res.json().catch(() => null);

    if (!res.ok || !data?.access_token) return false;

    if ("refresh_token" in data) {
      localStorage.setItem("refresh_token", data.refresh_token);
    }

    return true;
  } catch {
    return false;
  }
}

export default fetcher;
